/**
 * G-code, by way of three.js's own `GCodeLoader`, rebuilt into the object that was printed.
 *
 * A wrapper rather than an interpreter: the loader walks the program and produces line
 * segments, and this takes what it built and makes it fit the rest of the application.
 * None of what follows involves understanding G-code.
 *
 * ## Why a solid and not the path
 *
 * Drawn as the path itself — a line per move, however thick — a real print is hundreds of
 * thousands of segments of transparent geometry stacked on top of each other, which is
 * both unreadable and far too slow to walk around in a headset. But the path contains the
 * shape: a slicer produced each layer's outer perimeter by intersecting a solid with a
 * plane, so that perimeter *is* a cross-section of the original object. `domain/contours`
 * recovers those cross-sections and `domain/loft` joins them into a surface, which is what
 * gets drawn — an ordinary shaded mesh like every other model, an order of magnitude less
 * geometry, and a picture of the thing rather than of the machine's movements.
 *
 * ## The extruded lines only
 *
 * The loader returns two sets of lines: where plastic was laid down, and where the head
 * crossed empty air between one part of the print and the next. They are told apart by
 * their material's name, which is the only handle it offers. Only the extrusions are kept
 * — travel moves turn anything with more than one island into a cage of diagonals with the
 * object somewhere inside, and, counted in the bounds, a single move out to a park position
 * would decide how big the model is thought to be.
 *
 * ## Standing it up
 *
 * A printer's Z is up; the viewer's Y is up. The loader expresses that as a quarter turn on
 * the group it hands back, which is no use here: this application bakes a centering offset
 * into the geometry and lets each presentation mode own the transform above it. So the
 * rotation is baked in too, and what comes out is in scene axes like every other format.
 *
 * ## What the loader does not do
 *
 * It implements `G0`/`G1`, `G90`/`G91` and `G92`, and nothing else that bears on the shape.
 * In particular it has no `M83`, the relative-extrusion mode that PrusaSlicer and
 * SuperSlicer write by default: under it each `E` is a small amount rather than a running
 * total, and the loader compares each against the last as though both were totals. The
 * result is that an arbitrary third or so of the moves are taken for extrusions and the
 * rest for travel — a print full of holes that still looks like a print, which is a worse
 * failure than drawing nothing. It also has no `G20`, so an inch program is drawn 25.4
 * times too small, and no `G2`/`G3`, so arcs — common in machine-tool G-code, rare in FDM —
 * are skipped.
 *
 * These are pinned by tests in `toolpath.test.ts` rather than worked around here, so that
 * the behaviour is known rather than surprising.
 *
 * ## Measuring
 *
 * The bounds and the counts are read off the geometry that was built, rather than gathered
 * while parsing. That is the price of not doing the parsing: it means one pass more than is
 * strictly needed, over data already in memory, which is cheap next to reading the file.
 */

import {
  Box3,
  BufferGeometry,
  Float32BufferAttribute,
  LineSegments,
  Material,
  ShapeUtils,
  Vector2,
} from 'three';
import { GCodeLoader } from 'three/addons/loaders/GCodeLoader.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

import { extractChains, type Chain, type Contour } from '../domain/contours.js';
import { isSubstantial, loftChain } from '../domain/loft.js';
import type { Bounds, ModelStats, Vec3 } from '../domain/types.js';

/** The name `GCodeLoader` gives the material it draws extrusions with. */
const EXTRUDED_MATERIAL = 'extruded';

/**
 * A length below which a distance is nothing, in millimetres.
 *
 * A tenth of a micron: far below any move a printer makes, and far above the floating-point
 * residue left by rotating the coordinates.
 */
const NEGLIGIBLE_MM = 1e-4;

export type ToolpathReading =
  | {
      readonly ok: true;
      readonly stats: ModelStats;
      /** The reconstructed surface of the printed object, in scene axes. */
      readonly geometry: BufferGeometry;
    }
  | { readonly ok: false; readonly message: string };

export function readToolpath(text: string): ToolpathReading {
  const parsed = new GCodeLoader().parse(text);

  // The loader's quarter turn lives on the group; bringing the matrices up to date is what
  // makes it available to bake into the children below.
  parsed.updateMatrixWorld(true);

  const extruded: BufferGeometry[] = [];
  parsed.traverse((child) => {
    if (!isLine(child)) return;
    if (child.material.name !== EXTRUDED_MATERIAL) return;

    const geometry = child.geometry.clone();
    geometry.applyMatrix4(child.matrixWorld);
    extruded.push(geometry);
  });

  const positions = extruded.reduce(
    (total, geometry) => total + geometry.getAttribute('position').count,
    0,
  );

  if (positions === 0) {
    return {
      ok: false,
      message: 'No extrusions were found in this G-code, so there is nothing to show.',
    };
  }

  const merged = extruded.length === 1 ? extruded[0] : mergeGeometries(extruded);
  if (!merged) {
    return { ok: false, message: 'This G-code could not be drawn.' };
  }

  const path = withoutStrays(merged);
  const drawn = path.getAttribute('position').count;
  if (drawn === 0) {
    return {
      ok: false,
      message: 'No extrusions were found in this G-code, so there is nothing to show.',
    };
  }

  const segments = new Float32Array(path.getAttribute('position').array);
  path.dispose();

  const chains = extractChains(segments).filter((chain) => isSubstantial(chain));
  const geometry = buildSurface(chains);

  if (geometry.getAttribute('position').count === 0) {
    return {
      ok: false,
      message:
        'No closed outlines were found in this G-code, so the printed shape could not be worked out.',
    };
  }

  return {
    ok: true,
    stats: {
      kind: 'gcode',
      encoding: 'ascii',
      triangleCount: geometry.getAttribute('position').count / 3,
      // Two points make a segment. Counted from the path rather than from the surface,
      // since it is a fact about the program rather than about what is drawn.
      segmentCount: drawn / 2,
      layerCount: countLayers(segments),
      // Measured from the reconstruction, not from the path: a skirt is printed round the
      // object and is not part of it, and left in it would decide the model's size.
      bounds: boundsOf(geometry),
    },
    geometry,
  };
}

/**
 * Join every chain of outlines into one surface.
 *
 * The ends are capped so the object is not an open shell: without it, looking down at a
 * print shows the inside of its own walls. Holes are cut out of the caps at each end, and
 * only the ends need it — everywhere else the next layer's band carries on from this one.
 */
function buildSurface(chains: readonly Chain[]): BufferGeometry {
  const positions: number[] = [];
  const thickness = layerThickness(chains);

  for (const chain of chains) {
    // A stack of one has nothing to be joined to. Rather than drop it — which would leave
    // a print only a layer or two tall with nothing to show at all — it is given the
    // thickness of a layer, which is what it has.
    const standing =
      chain.contours.length > 1
        ? chain
        : { ...chain, contours: raised(chain, thickness) };

    // Appended one at a time rather than spread into `push`. A print's worth of numbers
    // passed as arguments overflows the call stack, and it does so only on files large
    // enough that nobody would be testing with them.
    for (const value of loftChain(standing)) positions.push(value);
  }

  const outlines = chains.filter((chain) => chain.kind === 'outline');
  const holes = chains.filter((chain) => chain.kind === 'hole');

  for (const outline of outlines) {
    const bottom = outline.contours[0];
    const top = outline.contours[outline.contours.length - 1];
    if (bottom) capAt(positions, bottom, holes, false);
    if (top && top !== bottom) capAt(positions, top, holes, true);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/** The same outline again, one layer higher: a plate rather than a sheet. */
function raised(chain: Chain, thickness: number): Contour[] {
  const only = chain.contours[0];
  if (!only) return [];
  return [only, { ...only, height: only.height + thickness }];
}

/**
 * How far apart the layers are, taken from the layers themselves.
 *
 * The commonest gap rather than the average, since the first layer is often thicker than
 * the rest and would drag a mean up. Falls back to a typical layer where there is only one.
 */
function layerThickness(chains: readonly Chain[]): number {
  const heights = [
    ...new Set(
      chains.flatMap((chain) => chain.contours.map((contour) => contour.height)),
    ),
  ].sort((a, b) => a - b);

  const gaps = heights
    .slice(1)
    .map((height, index) => height - (heights[index] ?? 0))
    .filter((gap) => gap > 1e-6)
    .sort((a, b) => a - b);

  return gaps[Math.floor(gaps.length / 2)] ?? 0.2;
}

/**
 * Close off one end of a chain, cutting out any holes that reach it.
 *
 * `ShapeUtils.triangulateShape` is three's own ear-clipping, which is exactly this job and
 * not one worth doing again here.
 */
function capAt(
  positions: number[],
  contour: Contour,
  holes: readonly Chain[],
  facingUp: boolean,
): void {
  const outer = contour.points.map(([x, z]) => new Vector2(x, z));

  const through = holes
    .map((hole) =>
      hole.contours.find(
        (candidate) => Math.abs(candidate.height - contour.height) < 1e-6,
      ),
    )
    .filter((found): found is Contour => found !== undefined)
    .map((found) => found.points.map(([x, z]) => new Vector2(x, z)));

  const faces = ShapeUtils.triangulateShape(outer, through);
  const all = [...outer, ...through.flat()];

  for (const [a, b, c] of faces) {
    if (a === undefined || b === undefined || c === undefined) continue;

    // Wound so the cap faces out of the object: up at the top, down at the bottom.
    for (const index of facingUp ? [a, c, b] : [a, b, c]) {
      const point = all[index];
      if (!point) continue;
      positions.push(point.x, contour.height, point.y);
    }
  }
}

/**
 * Drop the segments that are not lines of material.
 *
 * Priming and un-retracting push filament in without moving the head; the loader sees the
 * extruder advance and emits a segment from a point to itself. As a hairline that is
 * invisible, which is why it went unnoticed for so long — but drawn at the width of a real
 * nozzle it becomes a blob of material sitting where the printer only paused. A segment
 * with no length is not a line of plastic whatever the file says, so it goes.
 *
 * Nothing else is second-guessed. In particular the loader starts at `0, 0, 0` and does
 * not implement `G28`, so a program that extrudes before moving in x and y is drawn from
 * the corner of the bed — but on a homed machine that is where the head actually is, and
 * telling "the head was deliberately left there" from "the loader assumed it" cannot be
 * done from the geometry. It would need the program read, which is the library's job.
 *
 * Filtering the loader's output rather than replacing its parsing: the file is still read
 * entirely by the library.
 */
function withoutStrays(geometry: BufferGeometry): BufferGeometry {
  const position = geometry.getAttribute('position');
  const kept: number[] = [];

  for (let index = 0; index < position.count; index += 2) {
    const ax = position.getX(index);
    const ay = position.getY(index);
    const az = position.getZ(index);
    const bx = position.getX(index + 1);
    const by = position.getY(index + 1);
    const bz = position.getZ(index + 1);

    if (Math.hypot(bx - ax, by - ay, bz - az) < NEGLIGIBLE_MM) continue;

    kept.push(ax, ay, az, bx, by, bz);
  }

  const filtered = new BufferGeometry();
  filtered.setAttribute('position', new Float32BufferAttribute(kept, 3));
  return filtered;
}

/**
 * Whether this is something drawn as lines, said in a way that keeps its parts typed.
 *
 * `instanceof` on its own narrows to type arguments three leaves open, which makes
 * everything reached through it untyped — including the material name this depends on.
 */
function isLine(child: object): child is LineSegments<BufferGeometry, Material> {
  return child instanceof LineSegments;
}

function boundsOf(geometry: BufferGeometry): Bounds {
  const box = new Box3().setFromBufferAttribute(
    geometry.getAttribute('position') as never,
  );

  const min: Vec3 = [box.min.x, box.min.y, box.min.z];
  const max: Vec3 = [box.max.x, box.max.y, box.max.z];
  return { min, max };
}

/**
 * How many distinct heights the print was laid down at.
 *
 * Counted off the drawn vertices rather than tracked while parsing, for the same reason
 * everything else here is. Rounded to the micron, which is finer than any layer height and
 * coarse enough that floating-point noise does not split one layer into two.
 */
function countLayers(segments: Float32Array): number {
  const heights = new Set<number>();

  for (let at = 1; at < segments.length; at += 3) {
    heights.add(Math.round((segments[at] ?? 0) * 1000));
  }

  return heights.size;
}
